import * as Sentry from '@sentry/vue'

import { getErrorDetails, getErrorStatus, isUnauthorizedError } from '@layers/web/utils/error.ts'
import type {
  AgentCookie,
  AgentCalls,
  ResellerResponse,
  CurrentAgentCallsResponse,
} from '@layers/web/services/types/agent-api.ts'

export class AgentApiService {
  static async fetchResellerCookie (agentCode: string): Promise<AgentCookie> {
    const { market } = useRuntimeConfig().public

    try {
      const { data: result } = await apiFetch<ResellerResponse>(`/${market}/reseller/${agentCode}`)

      const { show_officetools, show_agenttools, translations_tools, cms_tools, cookietime } = result

      return {
        code: agentCode,
        currentDate: Date.now().toString(),
        maxAge: cookietime,
        officetools: show_officetools,
        agenttools: show_agenttools ? agentCode : false,
        translationtools: translations_tools ?? false,
        cmstools: cms_tools || false,
      }
    } catch (error) {
      const status = getErrorStatus(error)

      // An unknown or retired code (401/403) and a request without an answer are expected; only an answered
      // failure of the backend is worth a report
      if (status !== undefined && !isUnauthorizedError(error)) {
        Sentry.captureMessage('Failed to get agentCookie', {
          level: 'info',
          contexts: {
            details: { id: agentCode, ...getErrorDetails(error) },
          },
          tags: { type: 'UX', status: String(status) },
        })
      }

      throw error
    }
  }

  static async fetchCurrentAgentCalls (): Promise<AgentCalls | void> {
    const { market } = useRuntimeConfig().public
    const dayjs = useDayjs()

    try {
      const { data: result } = await apiFetch<CurrentAgentCallsResponse>(
        `/${market}/current-calls-for-agent`
      )

      const { error, ...agentCall } = result

      if (error) {
        logFailedHttpRequest(error)
        return
      }

      return {
        ...agentCall,
        time: dayjs().unix(),
      }
    } catch (error) {
      logFailedHttpRequest(error)
    }
  }
}
